import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '@env/environment';
import { AuthService } from '@app/auth/auth.service';

interface TenantFlowsDto {
  enabled_flows: string[];
  notifications_enabled?: boolean;
}

/**
 * Resolves the caller's contracted flows (tenant.enabled_flows) once per session.
 *
 * A manager only sees the pages of the flows its tenant contracted; MASTER is
 * unrestricted. `flows() === null` means unrestricted (MASTER, or load failure —
 * fail open so a transient error never locks the user out; the backend still
 * enforces data scoping).
 */
@Injectable({ providedIn: 'root' })
export class TenantFlowsService {
  private readonly http = inject(HttpClient);
  private readonly authService = inject(AuthService);

  private readonly _flows = signal<string[] | null>(null);
  // Capacidad, no flujo: se resuelve aparte a propósito. Meterla en `flows`
  // sería cómodo y volvería a mezclar los dos ejes que este cambio separó.
  // null = sin restricción (MASTER o carga fallida), igual que `flows`.
  private readonly _notifications = signal<boolean | null>(null);
  private readonly _loaded = signal(false);
  private loading: Promise<void> | null = null;

  readonly flows = this._flows.asReadonly();
  readonly loaded = this._loaded.asReadonly();

  ensureLoaded(): Promise<void> {
    if (this._loaded()) {
      return Promise.resolve();
    }
    if (!this.loading) {
      this.loading = this.load();
    }
    return this.loading;
  }

  /** True when the caller may use a feature gated by any of these flow keys. */
  allowsAny(flowKeys: string[] | undefined): boolean {
    if (!flowKeys || flowKeys.length === 0) {
      return true;
    }
    const flows = this._flows();
    if (flows === null) {
      return true; // unrestricted (MASTER) or not resolved — fail open
    }
    return flowKeys.some(key => flows.includes(key));
  }

  /** True cuando el tenant contrató la capacidad de notificaciones. */
  allowsNotifications(): boolean {
    return this._notifications() !== false;
  }

  private async load(): Promise<void> {
    try {
      const session = this.authService.session();
      if (!session || session.roles.includes('ROLE_MASTER')) {
        this._flows.set(null);
        this._notifications.set(null);
        return;
      }
      // A manager's GET /tenants returns only its own tenant.
      const tenants = await firstValueFrom(this.http.get<TenantFlowsDto[]>(`${environment.api}tenants`));
      this._flows.set(tenants[0]?.enabled_flows ?? []);
      // Ausente = backend viejo: fail open, como el resto del servicio.
      this._notifications.set(tenants[0]?.notifications_enabled ?? null);
    } catch {
      this._flows.set(null);
      this._notifications.set(null);
    } finally {
      this._loaded.set(true);
    }
  }
}
